import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import apiServices from "../../../../services/apiService";
import MetricCard from "../../../common/Card/MetricCard";
import Chart from "react-apexcharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faClock, faWrench, faBullhorn, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import ActionCenter from "../../../common/ActionCenter/ActionCenter";
import storageService from "../../../../utils/storageService";
import SiteAdminDashboard from "./SiteAdminDashboard";
import SuperAdminDashboard from "./SuperAdminDashboard";
import { primaryColor, secondaryColor } from "../../../../utils/comon";

const Overview = () => {
    const { user: authUser } = useSelector((state) => state.auth);

    const roleId = Number(authUser?.roleId || authUser?.RoleId || storageService.getItem("roleId"));
    const unitId = authUser?.branchorunitID;

    const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
    useEffect(() => {
        const handleThemeChange = () => {
            setTheme(localStorage.getItem("theme") || "light");
        };
        window.addEventListener("themeChanged", handleThemeChange);
        return () => window.removeEventListener("themeChanged", handleThemeChange);
    }, []);
    const isDark = theme === "dark";

    const [usersData, setUsersData] = useState([]);
    const [showAllTeamMembers, setShowAllTeamMembers] = useState(false);
    const [dashboardData, setDashboardData] = useState({
        userData: [],
        masters: {},
        users: {},
        visitors: {},
        notices: {},
        complaints: {},
        notifications: {}
    });

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                if (!unitId) return;

                const siteIdStr = authUser?.siteId || storageService.getItem("siteId") || 2;
                const response = await apiServices({
                    method: "GET",
                    url: `/CommDashboard/Dashboard/GetDashboardSummary?siteId=${siteIdStr}&roleId=${roleId}&unitId=${unitId}`
                });

                if (response?.isSuccess || response?.success) {
                    const payload = response.data?.data || response.data;
                    setDashboardData(payload);
                }
            } catch (error) {
                console.error("Error fetching unit admin dashboard data:", error);
            }
        };

        const fetchTeamMembers = async () => {
            if (roleId !== 4) return; // Call API for Unit Admin only

            try {
                const siteIdStr = authUser?.siteId || storageService.getItem("siteId") || 2;
                let url = `/CommUserDetails/getUserDetails?SiteId=${siteIdStr}`;

                if (unitId) {
                    url += `&UnitId=${unitId}`;
                }

                const response = await apiServices({
                    method: "GET",
                    url: url
                });

                if (response.success || response.data?.success) {
                    let resData = response?.data?.Commuserdetailsdata || [];
                    setUsersData(resData);
                }
            } catch (error) {
                console.error("Error fetching team members:", error);
            }
        };

        fetchDashboardData();
        fetchTeamMembers();
    }, [unitId]);

    // ----------------------------------------------------
    // METRICS CALCULATION
    // ----------------------------------------------------
    const { userData, users, visitors, notices, complaints } = dashboardData;

    const totalUsers = users?.totalUsers || 0;
    const totalVisitors = visitors?.totalVisitors || 0;
    const activeNotices = notices?.publishedNotices || 0;
    const openComplaints = complaints?.openComplaints || 0;

    const topMetrics = [
        {
            title: "Total Users",
            value: totalUsers,
            icon: faUsers,
            color: "text-blue-500"
        },
        {
            title: "Total Visitors",
            value: totalVisitors,
            icon: faUsers,
            color: "text-purple-500"
        },
        {
            title: "Active Notices",
            value: activeNotices,
            icon: faBullhorn,
            color: "text-green-500"
        },
        {
            title: "Open Complaints",
            value: openComplaints,
            icon: faWrench,
            color: "text-red-500"
        }
    ];

    // ----------------------------------------------------
    // CHARTS CONFIGURATION
    // ----------------------------------------------------
    const siteContext = {
        siteName: userData?.[0]?.siteName || "Your Site",
        cityName: userData?.[0]?.cityName || "City",
        adminName: userData?.[0]?.userName || authUser?.name || "Unit Admin",
        adminContact: userData?.[0]?.mobileNumber || "N/A",
        adminEmail: userData?.[0]?.email || "N/A"
    };

    const wingList = userData?.[0]?.wingDetails?.list || [];
    const unitList = userData?.[0]?.unitDetails?.list || [];

    // 1. Visitor Status Overview (Bar Chart)
    const visitorCategories = ['Approved', 'Pending', 'Checked In', 'Checked Out'];

    const lineChartOptions = {
        chart: { type: "bar", toolbar: { show: false } },
        plotOptions: {
            bar: { horizontal: false, columnWidth: '45%', borderRadius: 4 }
        },
        colors: [secondaryColor()],
        xaxis: {
            categories: visitorCategories,
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: isDark ? "#FFFFFF" : "#64748B"
                }
            }
        },
        legend: {
            position: "top",
            horizontalAlign: "left",
            labels: {
                colors: isDark ? "#FFFFFF" : "#64748B"
            }
        },
        dataLabels: { enabled: false }
    };

    const lineChartSeries = [
        { name: "Visitors", data: [visitors?.approvedVisitors || 0, visitors?.pendingVisitors || 0, visitors?.checkedInVisitors || 0, visitors?.checkedOutVisitors || 0] }
    ];

    // 2. Complaints Overview (Donut Chart)
    const columnChartOptions = {
        chart: { type: "donut" },
        labels: ["Open", "Processing", "Closed"],
        colors: [primaryColor(), secondaryColor(), "#10B981"],
        plotOptions: {
            pie: {
                donut: {
                    size: '75%',
                    labels: {
                        show: true,
                        name: {
                            show: true,
                            color: isDark ? '#94A3B8' : '#64748B',
                        },
                        value: {
                            show: true,
                            fontSize: '24px',
                            fontWeight: 'bold',
                            color: isDark ? '#FFFFFF' : '#0F172A',
                        },
                        total: {
                            show: true,
                            showAlways: true,
                            label: 'COMPLAINTS',
                            color: isDark ? '#94A3B8' : '#64748B',
                            formatter: function (w) {
                                return w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                            }
                        }
                    }
                }
            }
        },
        dataLabels: { enabled: false },
        legend: {
            position: "bottom",
            labels: {
                colors: isDark ? "#FFFFFF" : "#64748B"
            }
        },
    };

    const columnChartSeries = [complaints?.openComplaints || 0, complaints?.processingComplaints || 0, complaints?.closedComplaints || 0];

    // ----------------------------------------------------
    // QUICK ACCESS ACTIONS
    // ----------------------------------------------------
    const dashboardActions = [
        {
            icon: faUserPlus,
            title: "Invite Visitor",
            description: "Pre-register a new visitor",
            url: "/zentcomm/visitor-managment/invited-visitor",
            iconStyles: { bg: "bg-indigo-100", color: "text-indigo-600" }
        },
        {
            icon: faClock,
            title: "Attendance Logs",
            description: "Check and monitor user attendance",
            url: "/zentcomm/attendance",
            iconStyles: { bg: "bg-blue-100", color: "text-blue-600" }
        },
        {
            icon: faWrench,
            title: "Maintenance",
            description: "Manage maintenance requests and invoices",
            url: "/zentcomm/maintenance",
            iconStyles: { bg: "bg-green-100", color: "text-green-600" }
        },
        {
            icon: faBullhorn,
            title: "Announcements",
            description: "Post and view board announcements",
            url: "/zentcomm/announcement",
            iconStyles: { bg: "bg-purple-100", color: "text-purple-600" }
        }
    ];

    const currentDate = new Date();
    const currentHour = currentDate.getHours();
    const greeting = currentHour >= 5 && currentHour < 12 ? "Good Morning" : currentHour >= 12 && currentHour < 17 ? "Good Afternoon" : currentHour >= 17 && currentHour < 21 ? "Good Evening" : "Good Night";
    const formattedDate = currentDate.toLocaleString("en-US", { weekday: "long", day: "2-digit", month: "short", year: "numeric" });

    if (roleId === 2) {
        return <SuperAdminDashboard />;
    }

    if (roleId === 3) {
        return <SiteAdminDashboard />;
    }

    return (
        <div className="flex flex-col h-full overflow-hidden bg-dashboardbg">
            <div className="flex-1 overflow-y-auto p-4 lg:p-8 custom-scrollbar">
                <div className="max-w-[1600px] mx-auto w-full">

                    {/* Header */}
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-textprimary mb-1">
                            {greeting}, {authUser?.name || "-"}! 👋
                        </h1>
                        <p className="text-sm text-textsecondary">
                            {formattedDate} • Here is your unit overview
                        </p>
                    </div>

                    {/* Top Metric Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                        {topMetrics.map((metric, idx) => (
                            <MetricCard key={idx} {...metric} />
                        ))}
                    </div>

                    {/* Enterprise Plan (Site Context) */}
                    {siteContext && (
                        <div className="bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5 mb-6 flex flex-col md:flex-row items-center justify-between text-sm">
                            <div className="flex items-center gap-6">
                                <div className="w-14 h-14 bg-secondaryaccent text-primaryaccent border border-accentborder rounded-xl flex flex-col items-center justify-center font-bold text-xs">
                                    Logo
                                </div>
                                <div>
                                    <h2 className="text-sm font-bold text-textprimary flex items-center gap-2">
                                        {siteContext.siteName} <span className="bg-indigo-50 text-indigo-600 text-[10px] px-2 py-0.5 rounded-full font-semibold">Site</span>
                                    </h2>
                                    <div className="flex flex-wrap gap-x-8 gap-y-3 mt-3 text-xs">
                                        <div>
                                            <p className="text-textsecondary font-medium text-[10px] mb-1 uppercase">Location</p>
                                            <p className="font-semibold text-textprimary">{siteContext.cityName}</p>
                                        </div>
                                        <div>
                                            <p className="text-textsecondary font-medium text-[10px] mb-1 uppercase">Wings</p>
                                            <p className="font-semibold text-textprimary">{wingList.map(w => w.wingName).join(", ") || "-"}</p>
                                        </div>
                                        <div>
                                            <p className="text-textsecondary font-medium text-[10px] mb-1 uppercase">Units</p>
                                            <p className="font-semibold text-textprimary">{unitList.map(u => u.unitName).join(", ") || "-"}</p>
                                        </div>
                                        <div>
                                            <p className="text-textsecondary font-medium text-[10px] mb-1 uppercase">Unit Admin</p>
                                            <p className="font-semibold text-textprimary">{siteContext.adminName}</p>
                                        </div>
                                        <div>
                                            <p className="text-textsecondary font-medium text-[10px] mb-1 uppercase">Contact</p>
                                            <p className="font-semibold text-textprimary">{siteContext.adminContact}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Row 2: Visitor Activity & Team Members */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                        {/* Visitor Activity Bar Chart */}
                        <div className="lg:col-span-2 bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5">
                            <div className="flex justify-between items-center mb-1">
                                <h3 className="text-base font-bold text-textprimary">Visitor Status Overview</h3>
                                <span className="bg-red-50 text-red-500 text-[10px] px-2 py-1 rounded font-semibold flex items-center gap-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div> Live
                                </span>
                            </div>
                            <p className="text-xs text-textsecondary mb-4">Current status of visitors</p>
                            <div className="h-[300px]">
                                <Chart options={lineChartOptions} series={lineChartSeries} type="bar" height="100%" />
                            </div>
                        </div>

                        {/* Quick Access */}
                        <ActionCenter
                            title="Quick Access"
                            description="Quick shortcuts to key management workflows."
                            actions={dashboardActions}
                        />
                    </div>

                    {/* Row 3: Visitor Flow & Quick Access */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Complaints Donut Chart */}
                        <div className="lg:col-span-2 bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5">
                            <h3 className="text-base font-bold text-textprimary mb-1">Complaints Overview</h3>
                            <p className="text-xs text-textsecondary mb-4">Breakdown by status</p>
                            <div className="h-[300px] flex items-center justify-center">
                                <Chart options={columnChartOptions} series={columnChartSeries} type="donut" height="100%" />
                            </div>
                        </div>

                        {/* Team Members */}
                        <div className={`bg-cardbg rounded-xl shadow-sm border border-bordercolor p-5 ${showAllTeamMembers ? 'overflow-hidden flex flex-col h-[400px]' : 'h-full'}`}>
                            <div className="flex justify-between items-center mb-1">
                                <h3 className="text-base font-bold text-textprimary">Team members</h3>
                                <span className="bg-green-50 text-green-600 text-[10px] px-2 py-1 rounded font-semibold">ACTIVE</span>
                            </div>
                            <p className="text-[10px] text-textsecondary mb-4">{usersData.length} total users</p>

                            <div className={`${showAllTeamMembers ? 'flex-1 overflow-y-auto custom-scrollbar pr-2' : ''}`}>
                                <div className="space-y-4">
                                    {(showAllTeamMembers ? usersData : usersData.slice(0, 4)).map((user, idx) => {
                                        const colors = [
                                            "bg-orange-100 text-orange-600",
                                            "bg-blue-100 text-blue-600",
                                            "bg-green-100 text-green-600",
                                            "bg-purple-100 text-purple-600"
                                        ];
                                        const dotColors = ["bg-green-500", "bg-green-500", "bg-green-500", "bg-orange-400"];

                                        return (
                                            <div key={idx} className="flex items-center gap-3 py-1">
                                                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${colors[idx % 4]}`}>
                                                    {(user.firstName || user.FirstName)?.charAt(0) || ''}{(user.lastName || user.LastName)?.charAt(0) || ''}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-bold text-textprimary truncate">{user.firstName || user.FirstName} {user.lastName || user.LastName}</p>
                                                    <p className="text-[10px] text-textsecondary truncate">{user.roleName || user.RoleName || 'User'}</p>
                                                </div>
                                                <div className={`w-1.5 h-1.5 rounded-full ${dotColors[idx % 4]} shrink-0 mr-1`}></div>
                                            </div>
                                        );
                                    })}

                                    {/* Always show View More section for design matching when collapsed */}
                                    {!showAllTeamMembers && (
                                        <div
                                            className="flex items-center gap-3 py-1 mt-1 cursor-pointer hover:bg-appbg rounded-lg p-1 -mx-1 transition-colors"
                                            onClick={() => setShowAllTeamMembers(true)}
                                        >
                                            <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-[#262626] text-gray-600 dark:text-gray-300 flex items-center justify-center font-bold text-xs shrink-0">
                                                +{usersData.length > 4 ? usersData.length - 4 : 20}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-bold text-textprimary truncate">
                                                    {usersData.length > 4 ? usersData.length - 4 : 20} more members
                                                </p>
                                                <p className="text-[10px] text-textsecondary truncate">View all</p>
                                            </div>
                                            <div className="text-xs font-bold text-red-500 mr-1">View</div>
                                        </div>
                                    )}

                                    {/* Show View Less when expanded */}
                                    {showAllTeamMembers && (
                                        <div
                                            className="flex items-center justify-center py-2 mt-2 cursor-pointer hover:bg-appbg rounded-lg transition-colors text-xs font-bold text-textsecondary"
                                            onClick={() => setShowAllTeamMembers(false)}
                                        >
                                            View less
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default Overview;